import React, { useEffect, useState } from 'react';
import WidgetShell from '../dashboard/WidgetShell';
import TradingViewEmbed, { parseSymbols } from './TradingViewEmbed';
import './market.css';

/**
 * "Market" dashboard widgets: TradingView's free embeds for market context
 * (prices, charts, heatmaps, calendar, news). Same for every viewer, so they
 * can sit on personal and company dashboards alike.
 */

/*
 * TradingView's free widgets don't license every exchange: CME futures (ES1!,
 * NQ1!…), TVC:DXY and TVC:US10Y show "unavailable". The lists below were checked
 * in a live embed; the futures list uses OANDA prices that track the same
 * markets nearly around the clock.
 */
export const WATCHLISTS = {
  indices: {
    label: 'Major indices',
    symbols: [
      { proName: 'FOREXCOM:SPXUSD', title: 'S&P 500' },
      { proName: 'FOREXCOM:NSXUSD', title: 'Nasdaq 100' },
      { proName: 'FOREXCOM:DJI', title: 'Dow 30' },
      { proName: 'OANDA:US2000USD', title: 'Russell 2000' },
      { proName: 'TVC:GOLD', title: 'Gold' },
      { proName: 'TVC:USOIL', title: 'Crude oil' },
      { proName: 'BITSTAMP:BTCUSD', title: 'Bitcoin' },
    ],
  },
  futures: {
    label: 'Futures markets (ES, NQ, YM, RTY, GC, CL)',
    symbols: [
      { proName: 'OANDA:SPX500USD', title: 'S&P 500 · ES' },
      { proName: 'OANDA:NAS100USD', title: 'Nasdaq 100 · NQ' },
      { proName: 'OANDA:US30USD', title: 'Dow · YM' },
      { proName: 'OANDA:US2000USD', title: 'Russell · RTY' },
      { proName: 'OANDA:XAUUSD', title: 'Gold · GC' },
      { proName: 'OANDA:WTICOUSD', title: 'Crude · CL' },
    ],
  },
  megacaps: {
    label: 'Big tech',
    symbols: ['AAPL', 'MSFT', 'NVDA', 'AMZN', 'GOOGL', 'META', 'TSLA'].map((t) => ({ proName: `NASDAQ:${t}`, title: t })),
  },
  crypto: {
    label: 'Crypto',
    symbols: [
      { proName: 'BITSTAMP:BTCUSD', title: 'Bitcoin' },
      { proName: 'BITSTAMP:ETHUSD', title: 'Ethereum' },
      { proName: 'COINBASE:SOLUSD', title: 'Solana' },
      { proName: 'COINBASE:XRPUSD', title: 'XRP' },
      { proName: 'COINBASE:DOGEUSD', title: 'Dogecoin' },
    ],
  },
};

function watchlistSymbols(config) {
  if (config?.watchlist === 'custom') {
    const list = parseSymbols(config.symbols).map((s) => {
      const proxy = futuresProxy(s);
      return proxy ? { proName: proxy.symbol, title: proxy.label } : { proName: s, title: s.split(':').pop() };
    });
    if (list.length) return list;
  }
  return (WATCHLISTS[config?.watchlist] || WATCHLISTS.indices).symbols;
}

/*
 * CME/CBOT/COMEX/NYMEX futures (NQ1!, MNQZ2026, ES1!…) can't be shown in a free
 * TradingView embed ("This symbol is only available on TradingView"). Swap them
 * for the OANDA price that tracks the same market nearly around the clock, as
 * the futures watchlist above does.
 */
const FUTURES_PROXY = [
  { roots: ['NQ', 'MNQ'], symbol: 'OANDA:NAS100USD', label: 'Nasdaq 100 · NQ' },
  { roots: ['ES', 'MES'], symbol: 'OANDA:SPX500USD', label: 'S&P 500 · ES' },
  { roots: ['YM', 'MYM'], symbol: 'OANDA:US30USD', label: 'Dow · YM' },
  { roots: ['RTY', 'M2K'], symbol: 'OANDA:US2000USD', label: 'Russell · RTY' },
  { roots: ['GC', 'MGC'], symbol: 'OANDA:XAUUSD', label: 'Gold · GC' },
  { roots: ['SI', 'SIL'], symbol: 'OANDA:XAGUSD', label: 'Silver · SI' },
  { roots: ['CL', 'MCL'], symbol: 'OANDA:WTICOUSD', label: 'Crude · CL' },
];
const FUTURES_EXCHANGES = /^(CME|CME_MINI|CBOT|CBOT_MINI|COMEX|COMEX_MINI|NYMEX|NYMEX_MINI)$/i;

/** { symbol, label } for a CME-style futures symbol, or null when it's fine as is. */
export function futuresProxy(raw) {
  const text = String(raw || '').trim().toUpperCase();
  if (!text) return null;
  const [exchange, ticker] = text.includes(':') ? text.split(':') : ['', text];
  if (exchange && !FUTURES_EXCHANGES.test(exchange)) return null;
  // NQ1!, NQ2!, NQZ2026, NQZ26, MNQ1!
  const m = ticker.match(/^([A-Z0-9]+?)(\d!|[FGHJKMNQUVXZ]\d{2,4})$/);
  if (!m) return null;
  const hit = FUTURES_PROXY.find((f) => f.roots.includes(m[1]));
  return hit ? { symbol: hit.symbol, label: hit.label } : null;
}

const symbolOf = (config, fallback) => {
  const raw = parseSymbols(config?.symbol, 1)[0] || fallback;
  return futuresProxy(raw)?.symbol || raw;
};

/** Subtitle: "Nasdaq 100 · NQ (OANDA:NAS100USD)" for a swapped futures symbol. */
const symbolLabel = (config, symbol) => {
  const proxy = futuresProxy(parseSymbols(config?.symbol, 1)[0]);
  return proxy ? `${proxy.label} (${symbol})` : symbol;
};

/* Widgets ----------------------------------------------------------------- */

export function MarketTickerTapeWidget({ config }) {
  return (
    <div className="dashboard-widget mk-tape-widget">
      <TradingViewEmbed
        widget="ticker-tape"
        settings={{ symbols: watchlistSymbols(config), showSymbolLogo: true, displayMode: 'adaptive' }}
        label="Quotes by TradingView"
      />
    </div>
  );
}

/** One-tap symbols for the Chart widget (all available in TradingView's free embeds). */
export const CHART_QUICK_SYMBOLS = [
  { label: 'NQ', symbol: 'OANDA:NAS100USD' },
  { label: 'ES', symbol: 'OANDA:SPX500USD' },
  { label: 'YM', symbol: 'OANDA:US30USD' },
  { label: 'RTY', symbol: 'OANDA:US2000USD' },
  { label: 'GC', symbol: 'OANDA:XAUUSD' },
  { label: 'CL', symbol: 'OANDA:WTICOUSD' },
  { label: 'SPY', symbol: 'AMEX:SPY' },
  { label: 'QQQ', symbol: 'NASDAQ:QQQ' },
  { label: 'BTC', symbol: 'BITSTAMP:BTCUSD' },
];
const CHART_INTERVALS = [
  { value: '1', label: '1m' }, { value: '5', label: '5m' }, { value: '15', label: '15m' },
  { value: '60', label: '1h' }, { value: '240', label: '4h' }, { value: 'D', label: '1D' }, { value: 'W', label: '1W' },
];

/**
 * Live chart. The symbol and interval are picked here, not in TradingView's own
 * search: the embed never tells RiskLo what was picked there, so it reset on
 * every load. Here they save with the dashboard (or, on a dashboard this viewer
 * can't edit, just switch for now).
 */
export function MarketChartWidget({ config, onConfigChange }) {
  const [picked, setPicked] = useState({ symbol: config?.symbol || '', interval: config?.interval || '' });
  useEffect(() => { setPicked({ symbol: config?.symbol || '', interval: config?.interval || '' }); }, [config?.symbol, config?.interval]);
  const [typing, setTyping] = useState('');
  const effective = { ...config, symbol: picked.symbol || config?.symbol, interval: picked.interval || config?.interval };
  const symbol = symbolOf(effective, 'AMEX:SPY');
  const interval = effective.interval || 'D';
  const choose = (patch) => {
    setPicked((p) => ({ ...p, ...patch }));
    if (onConfigChange) onConfigChange(patch);
  };
  const submitTyped = (e) => {
    e.preventDefault();
    const s = parseSymbols(typing, 1)[0];
    if (s) { choose({ symbol: s }); setTyping(''); }
  };
  return (
    <WidgetShell title="Chart" subtitle={symbolLabel(effective, symbol)} bodyClassName="mk-body mk-chart-body">
      <div className="mk-chart-bar">
        <div className="mk-chart-chips" role="group" aria-label="Symbol">
          {CHART_QUICK_SYMBOLS.map((q) => (
            <button
              key={q.symbol}
              type="button"
              className={`mk-chip${q.symbol === symbol ? ' is-active' : ''}`}
              onClick={() => choose({ symbol: q.symbol })}
              title={q.symbol}
            >
              {q.label}
            </button>
          ))}
          <form className="mk-chart-other" onSubmit={submitTyped}>
            <input
              value={typing}
              onChange={(e) => setTyping(e.target.value)}
              placeholder="Other: AAPL, TSLA…"
              aria-label="Any symbol"
              maxLength={40}
            />
          </form>
        </div>
        <select
          className="mk-chart-interval"
          value={interval}
          onChange={(e) => choose({ interval: e.target.value })}
          aria-label="Interval"
        >
          {CHART_INTERVALS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
        </select>
      </div>
      <div className="mk-chart-frame">
        <TradingViewEmbed
          widget="advanced-chart"
          settings={{
            autosize: true,
            symbol,
            interval,
            timezone: 'America/New_York',
            style: '1',
            // Picked in the bar above, where it saves.
            allow_symbol_change: false,
            hide_side_toolbar: true,
            save_image: false,
            calendar: false,
            support_host: 'https://www.tradingview.com',
          }}
          label="Chart by TradingView"
        />
      </div>
    </WidgetShell>
  );
}

export function MarketSymbolWidget({ config }) {
  const symbol = symbolOf(config, 'FOREXCOM:SPXUSD');
  return (
    <WidgetShell title="Price" subtitle={symbol} bodyClassName="mk-body">
      <TradingViewEmbed
        widget="mini-symbol-overview"
        settings={{ symbol, width: '100%', height: '100%', dateRange: config?.range || '1M', autosize: true, largeChartUrl: '' }}
      />
    </WidgetShell>
  );
}

export const HEATMAP_OPTIONS = [
  { value: 'SPX500', label: 'S&P 500' },
  { value: 'NASDAQ100', label: 'Nasdaq 100' },
  { value: 'DJDJI', label: 'Dow 30' },
  { value: 'AllUSA', label: 'All US stocks' },
  { value: 'crypto', label: 'Crypto' },
];

export function MarketHeatmapWidget({ config }) {
  const source = config?.market || 'SPX500';
  const label = HEATMAP_OPTIONS.find((o) => o.value === source)?.label || 'S&P 500';
  const crypto = source === 'crypto';
  return (
    <WidgetShell title="Market heatmap" subtitle={`${label} · today`} bodyClassName="mk-body">
      <TradingViewEmbed
        key={crypto ? 'c' : 's'}
        widget={crypto ? 'crypto-coins-heatmap' : 'stock-heatmap'}
        settings={crypto ? {
          dataSource: 'Crypto', blockSize: 'market_cap_calc', blockColor: '24h_close_change|5',
          symbolUrl: '', hasTopBar: false, isDataSetEnabled: false, isZoomEnabled: true,
          hasSymbolTooltip: true, isMonoSize: false, width: '100%', height: '100%',
        } : {
          exchanges: [], dataSource: source, grouping: 'sector', blockSize: 'market_cap_basic', blockColor: 'change',
          symbolUrl: '', hasTopBar: false, isDataSetEnabled: false, isZoomEnabled: true,
          hasSymbolTooltip: true, isMonoSize: false, width: '100%', height: '100%',
        }}
      />
    </WidgetShell>
  );
}

export const CALENDAR_IMPORTANCE = [
  { value: '0,1', label: 'Medium and high impact' },
  { value: '1', label: 'High impact only' },
  { value: '-1,0,1', label: 'Everything' },
];
export const CALENDAR_COUNTRIES = [
  { value: 'us', label: 'United States' },
  { value: 'us,eu,gb,jp,cn,ca', label: 'Major economies' },
];

export function MarketCalendarWidget({ config }) {
  return (
    <WidgetShell title="Economic calendar" subtitle="CPI, jobs, Fed and more" bodyClassName="mk-body">
      <TradingViewEmbed
        widget="events"
        settings={{ width: '100%', height: '100%', importanceFilter: config?.importance || '0,1', countryFilter: config?.countries || 'us' }}
      />
    </WidgetShell>
  );
}

export const NEWS_FEEDS = [
  { value: 'all', label: 'Top stories' },
  { value: 'stock', label: 'Stocks' },
  { value: 'futures', label: 'Futures' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'forex', label: 'Forex' },
  { value: 'symbol', label: 'One symbol' },
];

export function MarketNewsWidget({ config }) {
  const feed = config?.feed || 'all';
  const settings = { width: '100%', height: '100%', displayMode: 'regular' };
  let subtitle = NEWS_FEEDS.find((f) => f.value === feed)?.label || 'Top stories';
  if (feed === 'all') settings.feedMode = 'all_symbols';
  else if (feed === 'symbol') {
    settings.feedMode = 'symbol';
    settings.symbol = symbolOf(config, 'AMEX:SPY');
    subtitle = settings.symbol;
  } else {
    settings.feedMode = 'market';
    settings.market = feed;
  }
  return (
    <WidgetShell title="Market news" subtitle={subtitle} bodyClassName="mk-body">
      <TradingViewEmbed widget="timeline" settings={settings} label="News by TradingView" />
    </WidgetShell>
  );
}

export function MarketMoversWidget({ config }) {
  return (
    <WidgetShell title="Top movers" subtitle="US stocks · gainers, losers, most active" bodyClassName="mk-body">
      <TradingViewEmbed
        widget="hotlists"
        settings={{ dateRange: config?.range || '1D', exchange: 'US', showChart: true, largeChartUrl: '', showSymbolLogo: true, showFloatingTooltip: false, width: '100%', height: '100%' }}
      />
    </WidgetShell>
  );
}

export function MarketTechnicalsWidget({ config }) {
  const symbol = symbolOf(config, 'AMEX:SPY');
  return (
    <WidgetShell title="Technical rating" subtitle={symbol} bodyClassName="mk-body">
      <TradingViewEmbed
        widget="technical-analysis"
        settings={{ interval: config?.interval || '1D', width: '100%', height: '100%', symbol, showIntervalTabs: true, displayMode: 'single' }}
      />
    </WidgetShell>
  );
}
